import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { Message } from 'primereact/message';
import { flagCheckInApi } from '../../api/flagSystemApi';
import { flagError, formatFlagInstant } from '../../utils/flagSystem';
import { Field } from './FlagSystemCommon';

const reasonLabel = (reason) => ({
  BlockAllTime: 'Blocked all the time',
  WithinTimeAllowance: 'Within allowed time window',
  OutsideTimeAllowance: 'Outside allowed time window',
  ValidOverride: 'Allowed by existing override',
}[reason] || 'Unknown evaluation reason');
const pointOption = (point) => {
  const identity = [point.gate, point.tile].filter(Boolean).join(' — ') || `Access point ${point.accessPointId}`;
  return {
    ...point,
    disabled: !point.direction,
    label: point.direction ? `${identity} (${point.direction})` : `${identity} (direction not configured — unavailable)`,
  };
};
const checkError = (failure) => {
  const status = failure && failure.response && failure.response.status;
  if (!failure || !failure.response) return 'The flags evaluation did not complete successfully. Its outcome is unknown; do not infer allowed or blocked.';
  const data = failure.response.data;
  const backendMessage = data && typeof data === 'object' && typeof (data.error || data.detail || data.title) === 'string' ?
    (data.error || data.detail || data.title) : typeof data === 'string' && data.trim() ? data : '';
  if (backendMessage) return backendMessage;
  if (status === 502) return 'Smart Tile access validation failed upstream. No access decision was returned.';
  if (status === 503) return 'Smart Tile access validation is unavailable or not configured. No access decision was returned.';
  if (status === 504) return 'Smart Tile access validation timed out. The outcome may be uncertain; do not retry automatically.';
  if (status === 409) return flagError(failure, 'The access-point direction changed or conflicts with this evaluation. Reload the access points and try again.');
  return flagError(failure, 'The flags evaluation did not complete successfully.');
};

const FlagCheckInDialog = ({ scope, locationName, memberId, onAccessError }) => {
  const [visible, setVisible] = useState(false);
  const [points, setPoints] = useState([]);
  const [loadingPoints, setLoadingPoints] = useState(false);
  const [selectedAccessPointId, setSelectedAccessPointId] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const requestVersion = useRef(0);
  const scopedContext = useMemo(() => ({ customerId: scope.customerId, locationId: scope.locationId }), [scope.customerId, scope.locationId]);
  const contextKey = `${scopedContext.customerId}:${scopedContext.locationId}:${memberId}`;
  const options = points.map(pointOption);
  const configured = options.filter(option => !option.disabled);
  const selected = configured.some(point => point.accessPointId === selectedAccessPointId) ? selectedAccessPointId : null;

  const close = () => {
    if (!submitting) {
      requestVersion.current += 1;
      setVisible(false); setPoints([]); setSelectedAccessPointId(null); setResult(null); setError('');
    }
  };
  const open = () => {
    requestVersion.current += 1;
    setVisible(true); setPoints([]); setSelectedAccessPointId(null); setResult(null); setError('');
  };
  useEffect(() => {
    if (!visible) return undefined;
    const version = requestVersion.current + 1;
    requestVersion.current = version;
    setLoadingPoints(true); setPoints([]); setSelectedAccessPointId(null); setResult(null); setError('');
    flagCheckInApi.accessPoints(scopedContext).then(loaded => {
      if (requestVersion.current !== version) return;
      setPoints(loaded);
      const available = loaded.filter(point => point.direction === 'Entry' || point.direction === 'Exit');
      setSelectedAccessPointId(available.length === 1 ? available[0].accessPointId : null);
    }).catch(failure => {
      if (requestVersion.current !== version) return;
      onAccessError(failure); setError(flagError(failure, 'Unable to load access points for this location.'));
    }).finally(() => {
      if (requestVersion.current === version) setLoadingPoints(false);
    });
    return () => { if (requestVersion.current === version) requestVersion.current += 1; };
  }, [visible, contextKey, scopedContext, onAccessError]);

  const submit = async () => {
    if (submitting || !selected) return;
    const version = requestVersion.current + 1;
    requestVersion.current = version;
    setSubmitting(true); setError(''); setResult(null);
    try {
      const evaluation = await flagCheckInApi.checkFlags(scopedContext, memberId, selected);
      if (requestVersion.current === version) setResult(evaluation);
    } catch (failure) {
      if (requestVersion.current !== version) return;
      onAccessError(failure); setError(checkError(failure));
    } finally {
      if (requestVersion.current === version) setSubmitting(false);
    }
  };
  const choosePoint = (accessPointId) => {
    setSelectedAccessPointId(accessPointId); setResult(null); setError('');
  };
  const selectedPoint = options.find(point => point.accessPointId === selected);

  return <>
    <Button label="Check in" icon="pi pi-user-plus" className="p-button-text" onClick={open} />
    <Dialog header="Check member flags" visible={visible} className="flag-editor flag-check-in-dialog"
      modal closable={!submitting} onHide={close}
      footer={<><Button label="Cancel" className="p-button-text" disabled={submitting} onClick={close} />
        <Button label="Check member in" icon="pi pi-search" className="p-button-success" loading={submitting}
          disabled={submitting || loadingPoints || !selected} onClick={submit} /></>}>
      <dl className="flag-check-in-context">
        <dt>Member ID</dt><dd>{memberId}</dd>
        <dt>Location</dt><dd>{locationName || `Location ${scopedContext.locationId}`}</dd>
      </dl>
      <Field id="check-in-access-point" label="Access point">
        <Dropdown inputId="check-in-access-point" value={selected} options={options} optionLabel="label" optionValue="accessPointId"
          optionDisabled="disabled" placeholder={loadingPoints ? 'Loading access points...' : 'Select an access point'}
          disabled={loadingPoints || !configured.length} onChange={event => choosePoint(event.value)} />
      </Field>
      {loadingPoints && <Message severity="info" text="Loading configured access points..." />}
      {!loadingPoints && !error && !points.length && <Message severity="warn" text="No access points are configured for this location. Flags cannot be checked." />}
      {!loadingPoints && points.length > 0 && !configured.length && <Message severity="warn" text="All listed access points have an unconfigured direction and are unavailable for flags checks." />}
      {!loadingPoints && configured.length > 1 && !selected && <Message severity="info" text="Select a configured access point before checking flags." />}
      {selectedPoint && <p className="flag-note">The server will use this access point’s configured {selectedPoint.direction} direction.</p>}
      {error && <Message severity="error" text={error} />}
      {result && <section className="flag-check-in-result" aria-live="polite">
        {result.status === 'FlagsBlocked' ? <Message severity="error" text="Blocked by flags. Smart Tile access validation was not called because flags blocked access. The member has not been checked in." /> :
          result.status === 'AccessAllowed' ? <><Message severity="success" text="Access allowed by Smart Tile." />
            {!result.checkedIn && <p className="flag-note">Attendance/check-in completion is not confirmed.</p>}</> :
          result.status === 'AccessDenied' ? <Message severity="error" text="Access denied by LMS, member is not active" /> :
          ['FlagsPassed', 'LmsNotConfigured'].includes(result.status) ? <Message severity="warn" text="The server returned a legacy or incomplete access result. Access approval is not confirmed; check the backend deployment and contract." /> :
            <Message severity="info" text="The server returned an unsupported evaluation result. Access approval and check-in are not confirmed." />}
        <p><strong>Access point:</strong> {selectedPoint && selectedPoint.label}<br />
          <strong>Evaluation time:</strong> {formatFlagInstant(result.checkedAtUtc)}</p>
        {typeof result.accessStatus === 'string' && result.accessStatus.trim() && <p><strong>Smart Tile status:</strong> {result.accessStatus}</p>}
        {result.status === 'FlagsBlocked' && result.flags && <><h5>Blocking flags</h5>
          <DataTable value={result.flags.triggeredFlags} emptyMessage="The server did not include blocking flag details." size="small">
            <Column field="flagId" header="Flag ID" /><Column field="memberFlagId" header="Assignment ID" />
            <Column header="Reason" body={flag => reasonLabel(flag.reason)} />
          </DataTable></>}
        {result.flags && <><h5>Evaluated flags</h5>
          <DataTable value={result.flags.evaluatedFlags} emptyMessage="No flags were evaluated." size="small">
            <Column field="flagId" header="Flag ID" /><Column field="memberFlagId" header="Assignment ID" />
            <Column header="Result" body={flag => <span>{flag.allowed ? 'Allowed' : 'Blocked'}</span>} />
            <Column header="Reason" body={flag => reasonLabel(flag.reason)} />
          </DataTable>
          {result.flags.notApplicableFlagIds.length > 0 && <p className="flag-note">Flag IDs {result.flags.notApplicableFlagIds.join(', ')} do not apply to the selected access-point direction.</p>}
        </>}
      </section>}
    </Dialog>
  </>;
};

export default FlagCheckInDialog;
